import { loadAssets } from './core/assets.js';
import { Loop } from './core/loop.js';
import { Screen } from './core/screen.js';
import { AudioSystem } from './audio/audio.js';
import { MenuScene, type MenuResult } from './ui/menu.js';
import { SimScene } from './scenes/simscene.js';
import { SIM_HZ } from './sim/sim.js';
import { profileStore } from './ui/profile.js';
import { NetworkScene } from './scenes/network.js';
import type { ArenaClient } from './net/arena.js';
import { ArenaScene } from './scenes/arena.js';
function fatal(message: string): void {
  const canvas = document.getElementById('screen') as HTMLCanvasElement | null;
  if (canvas) {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f33';
      ctx.font = '8px monospace';
      message.split('\n').forEach((line, i) => ctx.fillText(line, 2, 12 + i * 9));
    }
  }
  console.error(message);
}
interface Scene {
  update(): void;
  render(fps: number): void;
  attach(): void;
  detach(): void;
}
async function boot(): Promise<void> {
  const canvas = document.getElementById('screen');
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error('#screen canvas missing');
  const screen = new Screen(canvas);
  const ctx = screen.ctx;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, 128, 128);
  ctx.fillStyle = '#fff';
  ctx.font = '8px monospace';
  ctx.fillText('LOADING...', 36, 66);
  const assets = await loadAssets((done, total) => {
    ctx.fillStyle = '#000';
    ctx.fillRect(30, 72, 68, 8);
    ctx.fillStyle = '#fff';
    ctx.fillRect(30, 72, Math.round((68 * done) / total), 8);
  });
  const audio = new AudioSystem();
  audio.bus.enabled = profileStore.profile.sound;
  audio.loadMusic(assets.manifest.trailer.segments, assets.manifest.trailer.segment_kinds);
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === 'm' || e.key === 'M') {
      audio.bus.enabled = !audio.bus.enabled;
      profileStore.setSound(audio.bus.enabled);
      if (!audio.bus.enabled) audio.music.stop();
    }
  });
  let scene: Scene;
  const loop = new Loop(
    SIM_HZ,
    () => scene.update(),
    (_alpha, fps) => scene.render(fps),
  );
  const pilotNames = assets.strings.tm.slice(3, 7);
  const seedParam = new URLSearchParams(location.search).get('seed');
  function showMenu(): void {
    scene?.detach();
    const menu = new MenuScene(
      screen,
      assets,
      audio,
      (res: MenuResult) => startFight(res),
      profileStore,
      () => showNetwork(),
    );
    scene = menu;
    scene.attach();
  }
  function showNetwork(): void {
    scene?.detach();
    scene = new NetworkScene(screen, assets, startArena, showMenu);
    scene.attach();
  }
  function startArena(client: ArenaClient): void {
    scene?.detach();
    scene = new ArenaScene(screen, assets, audio, client, showMenu);
    scene.attach();
  }
  function startFight(res: MenuResult): void {
    scene?.detach();
    const seed = seedParam === null ? res.seed : Number(seedParam) | 0;
    const params = new URLSearchParams(location.search);
    const sim = new SimScene(screen, assets, audio, {
      seed,
      opponentConfig: res.opponentPilot as 0 | 1 | 2 | 3,
      opponentName: assets.strings.tm[37 + res.opponentPilot] ?? '???',
      playerPilot: res.playerPilot,
      practice: res.practice ?? false,
      playerName: pilotNames[res.playerPilot] ?? '???',
      autopilot: params.has('demo'),
      scriptedHuman: params.get('script') === 'duel',
      fastForward: Number(params.get('ff')) || 0,
      ...(params.has('hold') ? { freezeAfter: Number(params.get('hold')) } : {}),
      onResult: (won, result) => {
        if (won) profileStore.recordWin(res.opponentPilot, result);
      },
      onExit: () => showMenu(),
    });
    scene = sim;
    scene.attach();
  }
  const menu = new MenuScene(
    screen,
    assets,
    audio,
    (res: MenuResult) => startFight(res),
    profileStore,
    () => showNetwork(),
  );
  if (new URLSearchParams(location.search).has('fight')) {
    startFight({ playerPilot: 0, opponentPilot: 3, seed: 777 });
  } else {
    scene = menu;
    scene.attach();
  }
  if (new URLSearchParams(location.search).has('debug')) {
    (
      window as Window & {
        biplanesDebug?: () => Record<string, unknown>;
      }
    ).biplanesDebug = () => {
      if (scene instanceof SimScene || scene instanceof ArenaScene) return scene.diagnostics();
      if (scene instanceof MenuScene)
        return {
          scene: 'menu',
          state: scene.currentState,
          entries: [...scene.currentEntries],
          profile: JSON.parse(JSON.stringify(profileStore.profile)),
        };
      return { scene: 'network' };
    };
  }
  loop.start();
  if (new URLSearchParams(location.search).has('fpslog')) {
    setInterval(() => console.log('FPS', loop.currentFps), 1000);
  }
}
boot().catch((e: unknown) => fatal(e instanceof Error ? `${e.name}: ${e.message}` : String(e)));
